<script setup lang="ts">
import { useResizeObserver } from "@dsk/composables/useResizeObserver";
import { mediaWatchEventListenerPonyfill, ResizeObserverPolyfill as ResizeObserver } from "@dsk/polyfills";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  type StyleValue,
  useSlots,
  watch
} from "vue";

import { sleep } from "@/utils";

interface Props {
  hidden?: boolean;
  hideOnTargetClick?: boolean;
  marginBottom?: number;
  text?: string;
  textOnTargetClick?: string;
  delay?: number;
  triggerContainerClass?: StyleValue;
  tooltipContainerClass?: StyleValue;
  tooltipContentClass?: StyleValue;
  tooltipArrowClass?: StyleValue;
}

const props = withDefaults(defineProps<Props>(), {
  hidden: false,
  hideOnTargetClick: false,
  marginBottom: 0,
  text: "",
  textOnTargetClick: "",
  delay: 0,
  triggerContainerClass: "",
  tooltipContainerClass: "",
  tooltipContentClass: "",
  tooltipArrowClass: ""
});

const emit = defineEmits<{
  (event: "hide"): void;
  (event: "show"): void;
  (event: "click"): void;
  (event: "dblclick"): void;
}>();

const slots = useSlots();

const POSITION_TOP_DEFAULT = -1000;
const POSITION_LEFT_DEFAULT = -1000;

const text = computed<string>(() => {
  if (wasClicked.value && props.textOnTargetClick) {
    return props.textOnTargetClick;
  }

  return props.text;
});

const timeoutId = ref<null | number>(null);
const isHovering = ref(false);
const isVisible = ref(false);
const wasClicked = ref(false);
const wasDoubleClicked = ref(false);

const canDeviceHover = ref<null | boolean>(null);
const onDeviceHoverChange = (mq: unknown) => (canDeviceHover.value = (mq as MediaQueryList).matches === true);
const deviceHoverMediaWatch = window.matchMedia("(any-hover: hover)");

mediaWatchEventListenerPonyfill.addEventListener(deviceHoverMediaWatch, onDeviceHoverChange);

// Set initial device hover type
onMounted(() => onDeviceHoverChange(deviceHoverMediaWatch));

onBeforeUnmount(() => {
  mediaWatchEventListenerPonyfill.removeEventListener(deviceHoverMediaWatch, onDeviceHoverChange);
});

const triggerEl = ref<HTMLElement>();
const contentElement = ref<HTMLElement>();

const position = reactive({
  top: POSITION_TOP_DEFAULT,
  left: POSITION_LEFT_DEFAULT
});

const setPosition = (top: number, left: number) => {
  position.top = top;
  position.left = left;
};

const resetPosition = () => {
  setPosition(POSITION_TOP_DEFAULT, POSITION_LEFT_DEFAULT);
};

const updatePosition = () => {
  const elementConstraints = triggerEl.value?.getBoundingClientRect();
  const contentConstraints = contentElement.value?.getBoundingClientRect();

  if (elementConstraints === undefined || contentConstraints === undefined) {
    return resetPosition();
  }

  // If unsupported position somehow gets through
  let top = POSITION_TOP_DEFAULT;
  let left = POSITION_LEFT_DEFAULT;

  // Position is static for now as we only need top-tooltips
  const offsetHeight = contentConstraints.height + props.marginBottom;
  const offsetWidth = (contentConstraints.width - elementConstraints.width) / 2;
  top = elementConstraints.top - offsetHeight + window.scrollY;
  left = elementConstraints.left - offsetWidth + window.scrollX;

  setPosition(top, left);
};

const updatePositionAnimation = () => {
  requestAnimationFrame(updatePosition);
};

const hover = () => {
  isHovering.value = true;
  show();
};

const unhover = () => {
  isHovering.value = false;
  hide();
};

const show = () => {
  // Prevent double emit and re-calculation of position.
  if (isVisible.value) {
    return;
  }

  // Shouldn't show if the use explicitly disabled tooltip
  if (props.hidden) {
    return;
  }

  // An empty tooltip doesn't make sense
  // Consumer takes this responsibility when using slots, as we can't reliably read their contents
  if (!slots.content && props.text === "") {
    return;
  }

  // If we need to hide the tooltip when the target is clicked,
  // we will not ever be able to show the tooltip on a device that can't hover.
  if (props.hideOnTargetClick && !canDeviceHover.value) {
    return;
  }

  if (timeoutId.value) {
    return;
  }

  timeoutId.value = setTimeout(showWithoutDelay, props.delay);
};

const hide = () => {
  clearTimeoutDelay();

  isVisible.value = false;
  wasClicked.value = false;
  wasDoubleClicked.value = false;
  resetPosition();
  emit("hide");
};

const showWithoutDelay = () => {
  clearTimeoutDelay();

  isVisible.value = true;
  updatePositionAnimation();
  emit("show");
};

const clearTimeoutDelay = () => {
  if (timeoutId.value === null) {
    return;
  }

  clearTimeout(timeoutId.value);
  timeoutId.value = null;
};

const click = async () => {
  await sleep(250);

  if (wasDoubleClicked.value || isHovering.value === false) {
    return;
  }

  if (props.hidden && props.hideOnTargetClick) {
    emit("click");
    hide();
    return;
  }

  wasClicked.value = true;

  if (props.delay) {
    emit("click");
    showWithoutDelay();
  }
};

const doubleClick = () => {
  wasDoubleClicked.value = true;
  emit("dblclick");
};

// React properly to window size changes
const bodyElement = ref(document.body);
const { width, height } = useResizeObserver(bodyElement);
watch([width, height], updatePositionAnimation);

// Respond to content changes
watch(
  () => props.text,
  () => {
    updatePositionAnimation();
  }
);

const contentObserver = new ResizeObserver(() => {
  updatePositionAnimation();
});
onUnmounted(() => {
  contentObserver.disconnect();
});
watch(isVisible, async (value) => {
  if (!contentElement.value) {
    return;
  }

  if (value) {
    await nextTick(); // Make sure the contentElement ref is rendered and not null.
    contentObserver.observe(contentElement.value);
  } else {
    contentObserver.unobserve(contentElement.value);
  }
});
</script>

<template>
  <div class="le-tooltip">
    <div
      ref="triggerEl"
      :class="props.triggerContainerClass"
      @mouseover="hover()"
      @mouseleave="unhover()"
      @blur="unhover()"
      @click="click()"
      @dblclick="doubleClick()"
    >
      <slot />
    </div>
    <Teleport to="body">
      <div
        v-if="isVisible"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
        class="le-tooltip__content-container"
        :class="props.tooltipContainerClass"
      >
        <div ref="contentElement" class="le-tooltip__content" :class="props.tooltipContentClass">
          <slot name="content">{{ text }}</slot>
        </div>
        <div class="le-tooltip__arrow" :class="props.tooltipArrowClass"></div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

@z-index: 1100;

.le-tooltip {
  display: inline-flex;
  position: relative;

  &__content-container {
    position: absolute;
    z-index: @z-index;
    max-width: 250px;
    text-align: center;
    pointer-events: none;
  }

  &__content {
    padding: 6px 10px;
    border-radius: 4px;
    background-color: @colors[@neutrals][100];
    box-shadow: @shadows[dropdown];
    font-size: 14px;
    line-height: 20px;
  }

  &__arrow {
    position: absolute;
    z-index: @z-index;
    bottom: -13px;
    left: calc(50% - 14px);
    width: 0;
    height: 0;
    margin: 6px;
    border-width: 8px 8px 0 8px;
    border-style: solid;
    border-top-color: @colors[@neutrals][100];
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }
}
</style>
